import { API_ENDPOINTS } from '@/constants';
import { getApiBaseUrl, getApiHeaders } from '@/store/api/headers';
import type { TransferSseEvent } from '@/types/agents';

type TransferListener = (event: TransferSseEvent) => void;

function parseTransferSseBlock(block: string): unknown | null {
  const dataLines = block
    .split('\n')
    .map(l => l.trim())
    .filter(line => line.startsWith('data:'))
    .map(line => line.slice(5).trim());

  if (dataLines.length === 0) return null;

  const dataRaw = dataLines.join('\n');
  try {
    return JSON.parse(dataRaw);
  } catch {
    return null;
  }
}

class TransferSseService {
  private listeners = new Set<TransferListener>();
  private abortController: AbortController | null = null;
  private reconnectTimer: ReturnType<typeof setTimeout> | null = null;
  private reconnectAttempt = 0;
  private manuallyStopped = false;
  private processedKeys = new Set<string>();
  private processedOrder: string[] = [];
  private readonly maxProcessed = 500;

  subscribe(listener: TransferListener): () => void {
    this.manuallyStopped = false;

    this.listeners.add(listener);
    if (this.listeners.size === 1) {
      this.start();
    }
    return () => {
      this.listeners.delete(listener);
      if (this.listeners.size === 0) {
        this.stop();
      }
    };
  }

  private start(): void {
    this.manuallyStopped = false;
    this.connect();
  }

  private stop(): void {
    this.manuallyStopped = true;
    this.abortController?.abort();
    this.abortController = null;
    if (this.reconnectTimer) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = null;
    }
    this.reconnectAttempt = 0;
  }

  private scheduleReconnect(): void {
    if (this.manuallyStopped || this.listeners.size === 0) return;
    const delayMs = Math.min(30000, 1000 * 2 ** this.reconnectAttempt);
    this.reconnectAttempt += 1;
    this.reconnectTimer = setTimeout(() => {
      this.connect();
    }, delayMs);
  }

  private getDedupeKey(event: TransferSseEvent): string {
    switch (event.type) {
      case 'init':
        return `${event.type}:${event.connectId}:${event.timestamp}`;
      case 'transfer_request':
      case 'transfer_response':
        return `${event.type}:${event.requestId}`;
      case 'warm_consult_connected':
        return `${event.type}:${event.initialContactId}:${
          event.answeringConnectId ?? event.answeringDisplayName ?? ''
        }`;
      default: {
        // Should be unreachable due to union typing, but keeps runtime safe.
        const anyEvent = event as unknown as Record<string, unknown>;
        return `${(anyEvent.type as string) ?? 'unknown'}:${String(
          anyEvent.requestId ?? ''
        )}`;
      }
    }
  }

  private markProcessed(event: TransferSseEvent): boolean {
    const key = this.getDedupeKey(event);
    if (this.processedKeys.has(key)) return false;
    this.processedKeys.add(key);
    this.processedOrder.push(key);
    if (this.processedOrder.length > this.maxProcessed) {
      const evicted = this.processedOrder.shift();
      if (evicted) this.processedKeys.delete(evicted);
    }
    return true;
  }

  private emit(event: TransferSseEvent): void {
    if (!this.markProcessed(event)) return;
    this.listeners.forEach(listener => listener(event));
  }

  private async connect(): Promise<void> {
    let aborted = false;
    try {
      if (this.reconnectTimer) {
        clearTimeout(this.reconnectTimer);
        this.reconnectTimer = null;
      }

      this.abortController?.abort();
      this.abortController = new AbortController();

      const headers = await getApiHeaders({ eventStream: true });
      const response = await fetch(
        `${getApiBaseUrl()}${API_ENDPOINTS.AGENTS.TRANSFERS_STREAM}`,
        {
          method: 'GET',
          headers,
          signal: this.abortController.signal,
        }
      );

      if (!response.ok || !response.body) {
        throw new Error(`SSE connection failed: ${response.status}`);
      }

      this.reconnectAttempt = 0;

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let streamEnded = false;

      while (!streamEnded) {
        const { done, value } = await reader.read();
        if (done) {
          streamEnded = true;
          break;
        }
        buffer += decoder.decode(value, { stream: true });

        // SSE events are separated by a blank line.
        const blocks = buffer.split('\n\n');
        buffer = blocks.pop() ?? '';

        for (const block of blocks) {
          const event = parseTransferSseBlock(block);
          if (!event) continue;

          if (typeof event !== 'object') continue;
          const typed = event as Record<string, unknown>;
          switch (typed.type) {
            case 'transfer_request':
            case 'transfer_response':
            case 'warm_consult_connected':
              // Backend is source-of-truth; payload is assumed to match the contract.
              console.log('[TransferSSE] event', typed);
              this.emit(event as TransferSseEvent);
              break;
            case 'init':
            case 'ping':
              // Keep-alive/handshake messages are intentionally ignored by consumers.
              break;
            default:
              // e.g. { type: 'ping', ... } -> ignore
              break;
          }
        }
      }
    } catch (error) {
      if (this.manuallyStopped) return;
      if (error instanceof DOMException && error.name === 'AbortError') {
        aborted = true;
        return;
      }
    } finally {
      // Keep stream always-on while the service has subscribers.
      if (!this.manuallyStopped && !aborted) this.scheduleReconnect();
    }
  }
}

export const transferSseService = new TransferSseService();
